// Free plan minutes, counted live while recording, and the dictations the
// server has not counted yet. Mirrors the Android app
// (features/voice/freeMinuteBudget.ts and features/upgrade/pendingFreeUsage.ts)
// so every platform shows the same number at the same moment.

/**
 * The recording readout stays hidden until this much of the week's allowance
 * is spent, and turns amber from the second threshold.
 */
export const QUOTA_SHOW_FROM_USED_FRACTION = 0.6;
export const QUOTA_ATTENTION_FROM_USED_FRACTION = 0.8;

/**
 * How long a Free recording keeps going after the allowance runs out, so the
 * user can finish their sentence. The user is told this number, so
 * GRACE_MESSAGE has to match it.
 */
export const GRACE_MS = 60 * 1000;
export const GRACE_MESSAGE =
  "Your minutes are exhausted. Your recording will automatically close in one minute.";

/**
 * The recording readout for this instant, or null when there is nothing to
 * show (no allowance, or less than 60% of it used). `inGrace` is set once the
 * allowance is spent and the recording is counting down to its auto-stop.
 *
 * Returns { tone: "neutral" | "attention" | "empty", value, label }.
 */
export const describeFreeMinutes = (allowanceSec, remainingSec, inGrace = false) => {
  const label = "left this week";
  if (inGrace) return { tone: "empty", value: "No time", label };
  if (!(allowanceSec > 0) || remainingSec == null) return null;
  // Rounded, as Android does, so 80% of 15 minutes is exactly 3 minutes left.
  const showBelowSec = Math.round(allowanceSec * (1 - QUOTA_SHOW_FROM_USED_FRACTION));
  const attentionBelowSec = Math.round(allowanceSec * (1 - QUOTA_ATTENTION_FROM_USED_FRACTION));
  if (remainingSec > showBelowSec) return null;
  if (remainingSec <= 0) return { tone: "empty", value: "No time", label };
  const minutes = Math.floor(remainingSec / 60);
  return {
    tone: remainingSec <= attentionBelowSec ? "attention" : "neutral",
    // Rounded down so it is never overstated.
    value: minutes === 0 ? "<1 min" : `${minutes} min`,
    label,
  };
};

/* --------------------------- pending free usage --------------------------- */
// The realtime relay bills a dictation by posting its audio seconds after the
// transcript completes, without waiting for that post, so the subscription
// read right after a dictation often has the usage from before it. Each
// realtime dictation is recorded here and counted on top of the server's
// figure until a reading taken PENDING_SETTLE_MS after it, which is believed
// as is. A new weekly allowance clears it. File uploads are billed inside
// their own request, so they never need this.

const PENDING_KEY = "zt_free_usage_pending_v1";
/** Long enough for the relay's billing post to land after the transcript. */
export const PENDING_SETTLE_MS = 30_000;

const readPending = (scope) => {
  if (!scope) return null;
  try {
    const all = JSON.parse(localStorage.getItem(PENDING_KEY) || "{}");
    return all[scope] || null;
  } catch {
    return null;
  }
};

const writePending = (scope, pending) => {
  if (!scope) return;
  try {
    const all = JSON.parse(localStorage.getItem(PENDING_KEY) || "{}");
    if (pending) all[scope] = pending;
    else delete all[scope];
    localStorage.setItem(PENDING_KEY, JSON.stringify(all));
  } catch {
    /* storage unavailable: the server figure stands */
  }
};

/** The same instant written either way compares equal. */
const normalizeResetsAt = (value) => {
  if (!value) return null;
  const t = Date.parse(value);
  return Number.isNaN(t) ? String(value) : new Date(t).toISOString();
};

const total = (debits) => debits.reduce((sum, d) => sum + d.seconds, 0);

/**
 * The usage to believe for a server reading taken now: the server's own,
 * unless dictations it may not have counted yet put it higher. Retires the
 * debits the server has had time to count. Safe to call more than once for
 * the same reading.
 */
export const usedSecondsWithPending = (scope, serverUsedSeconds, resetsAt, now = Date.now()) => {
  const pending = readPending(scope);
  if (!pending) return serverUsedSeconds;
  if (pending.resetsAt !== normalizeResetsAt(resetsAt)) {
    writePending(scope, null);
    return serverUsedSeconds;
  }
  const open = pending.debits.filter((d) => now - d.endedAt < PENDING_SETTLE_MS);
  if (open.length === 0) {
    writePending(scope, null);
    return serverUsedSeconds;
  }
  // Settled debits are in the server's figure now; what it reports beyond the
  // open ones (another device's dictation, say) is part of the base too.
  const baseUsedSeconds = Math.max(
    pending.baseUsedSeconds + total(pending.debits) - total(open),
    serverUsedSeconds - total(open)
  );
  writePending(scope, { ...pending, baseUsedSeconds, debits: open });
  return Math.max(serverUsedSeconds, baseUsedSeconds + total(open));
};

/**
 * A realtime dictation the relay bills has just ended. `usedSeconds` and
 * `resetsAt` are the allowance as last known.
 */
export const addPendingFreeUsage = (scope, seconds, usedSeconds, resetsAt, now = Date.now()) => {
  if (!scope || !(seconds > 0)) return;
  const key = normalizeResetsAt(resetsAt);
  const existing = readPending(scope);
  const pending =
    existing && existing.resetsAt === key
      ? existing
      : { resetsAt: key, baseUsedSeconds: usedSeconds, debits: [] };
  pending.debits.push({ seconds, endedAt: now });
  writePending(scope, pending);
};
